import { normalizeChoices } from '@/utils'

/**
 * Creates a deterministic key for grouping products that includes nested options
 * This ensures products with the same base options but different nested options are grouped separately
 */
function createGroupingKey(normalizedOptions) {
	if (!normalizedOptions || !Array.isArray(normalizedOptions)) {
		return JSON.stringify(normalizedOptions)
	}

	// Sort options by optionId to ensure consistent ordering
	const sortedOptions = [...normalizedOptions].sort((a, b) => {
		const aId = a.optionId || a.id || ''
		const bId = b.optionId || b.id || ''
		return aId.localeCompare(bId)
	})

	// Create a normalized structure that includes nested options
	const normalized = sortedOptions.map(option => {
		const optionId = option.optionId || option.id
		const chosenIds = option.chosenIds || (option.options ? option.options : [])

		// Process chosenIds to include nested options in a deterministic way
		const processedChosenIds = chosenIds.map(chosenId => {
			if (typeof chosenId === 'string') {
				return { id: chosenId, nestedOptions: [] }
			}
			if (typeof chosenId === 'object' && chosenId.id) {
				// Sort nested options by id for consistency
				const nestedOptions = Array.isArray(chosenId.options)
					? [...chosenId.options]
						.sort((a, b) => {
							const aId = typeof a === 'string' ? a : (a.id || '')
							const bId = typeof b === 'string' ? b : (b.id || '')
							return aId.localeCompare(bId)
						})
						.map(nested => {
							// Extract just the ID string, don't wrap in an object
							return typeof nested === 'string' ? nested : (nested.id || nested)
						})
					: []
				return {
					id: chosenId.id,
					nestedOptions: nestedOptions
				}
			}
			return chosenId
		})

		// Sort chosenIds by id for consistency
		processedChosenIds.sort((a, b) => {
			const aId = typeof a === 'string' ? a : (a.id || '')
			const bId = typeof b === 'string' ? b : (b.id || '')
			return aId.localeCompare(bId)
		})

		return {
			optionId: optionId,
			chosenIds: processedChosenIds
		}
	})

	return JSON.stringify(normalized)
}

// Function to group products by SKU + options + units for new format
export function groupProductsBySkuAndOptions(productsCart) {
	const groupedProducts = new Map()

	productsCart.forEach(product => {
		// Create a unique key based on SKU + normalized options + units (for unitary products)
		const normalizedOptions = normalizeChoices(product.chosenOptions)
		const units = product.isUnitary === 'SI' ? product.cartItemCount : null
		const key = `${product.sku}_${createGroupingKey(normalizedOptions)}_${units || 'non-unitary'}`

		if (groupedProducts.has(key)) {
			const existing = groupedProducts.get(key)

			// For unitary products, we don't group by quantity - each unit count is separate
			if (product.isUnitary === 'SI') {
				// For unitary products, each different unit count should be separate
				// This shouldn't happen with the current key, but just in case
				existing.orderQuantity += product.cartItemCount
			} else {
				// For non-unitary products, group by quantity
				existing.orderQuantity += product.cartItemCount
			}

			// Merge comments if they exist
			if (product.comments && product.comments.length > 0) {
				if (!existing.staffComments) {
					existing.staffComments = []
				}
				existing.staffComments.push(...product.comments)
			}
		} else {
			// Transform chosenOptions to ensure nested options have the correct structure
			const transformedOptions = Array.isArray(product.chosenOptions)
				? product.chosenOptions.map(option => ({
					optionId: option.optionId,
					chosenIds: Array.isArray(option.chosenIds)
						? option.chosenIds.map(chosenId => {
							// If it's just a string, return as is
							if (typeof chosenId === 'string') {
								return chosenId
							}
							// If it's an object with nested options
							if (typeof chosenId === 'object' && chosenId.id) {
								return {
									id: chosenId.id,
									options: Array.isArray(chosenId.options)
										? chosenId.options.map(nestedOption => {
											// Flatten nested structure - extract just the ID
											if (typeof nestedOption === 'string') {
												return { id: nestedOption }
											}
											if (typeof nestedOption === 'object' && nestedOption.id) {
												// If double-nested, extract the inner ID
												const actualId = typeof nestedOption.id === 'string'
													? nestedOption.id
													: nestedOption.id.id || nestedOption.id
												return { id: actualId }
											}
											return nestedOption
										})
										: []
								}
							}
							return chosenId
						})
						: []
				}))
				: product.chosenOptions

			const groupedProduct = {
				sku: product.sku,
				options: transformedOptions,
				orderQuantity: product.cartItemCount,
				modifyDishClass: product.modifyDishClass?.id,
				isShareable: product.isShareable
			}

			// Add units field for unitary products
			if (product.isUnitary === 'SI') {
				groupedProduct.units = product.cartItemCount
			}

			// Add staff comments if they exist
			if (product.comments && product.comments.length > 0) {
				groupedProduct.staffComments = [...product.comments]
			}

			groupedProducts.set(key, groupedProduct)
		}
	})

	return Array.from(groupedProducts.values())
}
